// Drives a "+ New X"-style popup-window creation flow, shared by any picker
// controller that needs to open a nav-free creation form in a real popup
// window, block the parent form with an overlay while it's open, and learn
// about a newly-created record via postMessage.
//
// Framework-agnostic on purpose - this has nothing Stimulus-specific in it,
// so it can be reused by picker controllers with otherwise-unrelated needs
// (different backing models, different "what do I do with the new record"
// logic). Construct one per picker instance, call open(popupUrl) from a
// click handler, and destroy() it from the controller's disconnect().
//
// The hard-won parts worth calling out, both found and fixed on 9065's
// Content picker:
// - event.origin is checked against this page's own origin before acting on
//   any message at all - is this message legitimate?
// - A per-open random token, round-tripped through the popup's own form and
//   echoed back in its postMessage payload, is checked separately - which
//   open popup (if more than one picker is on the page) does this message
//   belong to? Deliberately not done by comparing event.source to the popup
//   window reference: the popup posts its message and then immediately
//   calls window.close(), so popupWindow.closed can flip true - and some
//   browsers can surface a stale/null event.source - before the message
//   actually arrives here.
// - pollPopupClosed's "closed without ever posting a message" cleanup (e.g.
//   the user hit Cancel) waits a brief moment before concluding that and
//   clearing the token, since popupWindow.closed can flip true on the
//   success path too, right before its message is actually delivered - a
//   dead end here isn't necessarily a cancel.
export class PopupPicker {
  constructor(onRecordCreated) {
    this.onRecordCreated = onRecordCreated
    this.boundHandleMessage = this.handleMessage.bind(this)
    window.addEventListener("message", this.boundHandleMessage)
  }

  open(popupUrl) {
    this.currentToken = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    const url = new URL(popupUrl, window.location.origin)
    url.searchParams.set("popup_token", this.currentToken)

    this.popupWindow = window.open(url.toString(), "_blank", "width=900,height=700")
    this.showOverlay()
    this.pollClosed()
  }

  pollClosed() {
    this.clearPoll()
    this.pollInterval = setInterval(() => {
      if (this.popupWindow && this.popupWindow.closed) {
        this.clearPoll()

        setTimeout(() => {
          this.currentToken = null
          this.hideOverlay()
        }, 250)
      }
    }, 300)
  }

  clearPoll() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  handleMessage(event) {
    if (event.origin !== window.location.origin) return
    if (!event.data || event.data.type !== "popup:record-created") return
    if (!this.currentToken || event.data.token !== this.currentToken) return

    this.onRecordCreated(event.data)

    this.currentToken = null
    this.clearPoll()
    this.hideOverlay()
  }

  showOverlay() {
    if (this.overlayElement) return

    const overlay = document.createElement("div")
    overlay.className = "popup-picker-overlay"
    Object.assign(overlay.style, {
      position: "fixed",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      background: "rgba(255, 255, 255, 0.6)",
      zIndex: "1050"
    })

    document.body.appendChild(overlay)
    this.overlayElement = overlay
  }

  hideOverlay() {
    if (this.overlayElement) {
      this.overlayElement.remove()
      this.overlayElement = null
    }
  }

  destroy() {
    window.removeEventListener("message", this.boundHandleMessage)
    this.clearPoll()
    this.hideOverlay()
  }
}
